import { Disposable, Injectable, InjectableConstructor } from './Injectable';

export const SYSTEMS_TOKEN = Symbol.for('Systems');

export type SystemOperation = () => void;

export type SystemOperationGroup = 'unspecific' | 'viewMatrixChanged' | 'viewMatrixChangedEnd' | 'viewMatrixIdle' | 'beforeRender' | 'afterRender';

export type SystemOperationConstraint = SystemOperation | '*';

export type SystemOperationDefinition = {
  group?: SystemOperationGroup;
  before?: SystemOperationConstraint[];
  after?: SystemOperationConstraint[];
  run: SystemOperation;
};

export type OrganizedSystemOperations = {
  [key in SystemOperationGroup]?: SystemOperationDefinition[];
};

export interface BaseSystem extends Injectable {
  readonly operations: Readonly<SystemOperationDefinition[]>;
}

export type SystemConstructor<System extends BaseSystem = BaseSystem> = InjectableConstructor<System>;

/**
 * Organize system's operations to OrganizedSystemOperations by its group
 * The systemOperation in a group is sorted by resolving its before/after constraint graph
 */
export function organizeSystemOperations(systems: BaseSystem[]): OrganizedSystemOperations {
  const unsorted = systems.reduce((acc, system) => {
    return system.operations.reduce((acc, operation) => {
      const group = operation.group ?? 'unspecific';

      const operations = acc[group] ?? [];
      operations.push(operation);
      acc[group] = operations;

      return acc;
    }, acc);
  }, {} as OrganizedSystemOperations);

  const sorted = Object.entries(unsorted).reduce((acc, [group, operations]) => {
    const beforeWithWildcard: SystemOperationDefinition[] = [];
    const afterWithWildcard: SystemOperationDefinition[] = [];
    const withoutWildcard: SystemOperationDefinition[] = [];

    for (const op of operations) {
      const hasBeforeWildcard = op.before?.includes('*');
      const hasAfterWildcard = op.after?.includes('*');

      if (hasBeforeWildcard && hasAfterWildcard) {
        throw new Error('SystemOperationDefinition can not have wildcard `*` on both before/after constraints');
      } else if (hasBeforeWildcard) {
        beforeWithWildcard.push(op);
      } else if (hasAfterWildcard) {
        afterWithWildcard.push(op);
      } else {
        withoutWildcard.push(op);
      }
    }

    const sortedOperations = [
      ...sortOperations(beforeWithWildcard),
      ...sortOperations(withoutWildcard),
      ...sortOperations(afterWithWildcard),
    ];
    acc[group as keyof OrganizedSystemOperations] = sortedOperations;
    return acc;
  }, {} as OrganizedSystemOperations);

  return sorted;
}

/**
 * Sort operations by resolving its before/after constraint graph
 *
 * @remark
 *
 * Does not handle wildcard constraint
 */
export function sortOperations(operations: SystemOperationDefinition[]) {
  // Build the constraint graph
  const graph = new Map<SystemOperationConstraint, Set<SystemOperationConstraint>>();

  for (const operation of operations) {
    if (operation.before) {
      for (const before of operation.before) {
        const set = graph.get(before);
        if (!set) {
          graph.set(before, new Set([operation.run]));
        } else {
          set.add(operation.run);
        }
      }
    }
    if (operation.after) {
      for (const after of operation.after) {
        const set = graph.get(operation.run);
        if (!set) {
          graph.set(operation.run, new Set([after]));
        } else {
          set.add(after);
        }
      }
    }
  }

  // Topological sort
  const result: SystemOperationDefinition[] = [];
  const visited = new Set<SystemOperationConstraint>();

  const visit = (opsOrWildcard: SystemOperationConstraint) => {
    if (visited.has(opsOrWildcard)) {
      return;
    }
    visited.add(opsOrWildcard);

    const cons = graph.get(opsOrWildcard);
    if (cons) {
      for (const con of cons) {
        visit(con);
      }
    }

    const op = operations.find((op) => op.run === opsOrWildcard);
    if (op) {
      result.push(op);
    }
  };

  for (const operation of operations) {
    visit(operation.run);
  }

  return result;
}
